// src/pages/ShelfManagementPage.tsx
// 本棚編集画面（モバイルの manage_bookshelf_page.dart と同等の機能を Web に移植）
// 本棚の追加・名前/色/公開設定の変更・削除・並び替えをまとめて編集し、
// 「本棚編集完了」で一括保存する。

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import {
  ShelfDetail,
  fetchShelvesRaw,
  createShelf,
  updateShelf,
  deleteShelf,
  reorderShelves,
  setShelfBooksOpenLevel,
} from '../utils/shelfHandler';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';

const BASIC_COLORS = [
  'd33232', '80343b', '2c3643', '233048', '1a294d',
  '72839e', 'caddee', 'dad7bf', 'ead090', 'FFFFFF',
];

const OPEN_LEVEL_LABELS: Record<string, string> = {
  private: '非公開',
  public: '公開',
};

interface EditableShelf extends ShelfDetail {
  _isNew?: boolean;
}

const genTempId = () =>
  (window.crypto as any)?.randomUUID ? (window.crypto as any).randomUUID() : `tmp-${Date.now()}-${Math.random()}`;

const normalizeColor = (c: string) => (c.startsWith('#') ? c : `#${c}`);

const ShelfManagementPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [shelves, setShelves] = useState<EditableShelf[] | null>(null);
  const [originalIds, setOriginalIds] = useState<Set<string>>(new Set());
  const [originalOpenLevels, setOriginalOpenLevels] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // 追加/編集ダイアログの状態
  const [dialogState, setDialogState] = useState<{
    mode: 'add' | 'edit';
    shelfId?: string;
    name: string;
    colorCode: string;
    openLevel: string;
  } | null>(null);

  // 削除確認
  const [deleteTarget, setDeleteTarget] = useState<EditableShelf | null>(null);

  // 公開設定変更を本棚内の本にも揃えるかの確認（Promiseで結果を待つ）
  const [propagateConfirm, setPropagateConfirm] = useState<{
    shelfName: string;
    resolve: (v: boolean) => void;
  } | null>(null);

  // 未ログインならトップへ自動遷移。
  // isLoggedIn は Cognito セッション確認が終わるまで一瞬 false になるため
  // （特に新規タブで直接このページを開いた場合）、即座に遷移せず少し待つ。
  useEffect(() => {
    if (isLoggedIn) return;
    const t = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  const load = useCallback(async () => {
    if (!idToken) return;
    try {
      const items = await fetchShelvesRaw(idToken);
      items.sort((a, b) => (a.global_shelf_order ?? 0) - (b.global_shelf_order ?? 0));
      setOriginalIds(new Set(items.map((s) => s.rl_global_shelf_id)));
      setOriginalOpenLevels(
        Object.fromEntries(items.map((s) => [s.rl_global_shelf_id, s.global_shelf_open_level ?? 'private']))
      );
      setShelves(items);
    } catch (e) {
      setShelves([]);
      setSnackbar({ open: true, message: '本棚の取得に失敗しました', severity: 'error' });
    }
  }, [idToken]);

  useEffect(() => {
    load();
  }, [load]);

  const openAddDialog = () => {
    setDialogState({ mode: 'add', name: '', colorCode: BASIC_COLORS[0], openLevel: 'private' });
  };

  const openEditDialog = (shelf: EditableShelf) => {
    setDialogState({
      mode: 'edit',
      shelfId: shelf.rl_global_shelf_id,
      name: shelf.global_shelf_name,
      colorCode: (shelf.global_shelf_color_code || '#808080').replace('#', ''),
      openLevel: shelf.global_shelf_open_level ?? 'private',
    });
  };

  const handleDialogConfirm = () => {
    if (!dialogState || !shelves) return;
    const name = dialogState.name.trim();
    if (!name) {
      setDialogState(null);
      return;
    }
    if (dialogState.mode === 'add') {
      const newShelf: EditableShelf = {
        rl_global_shelf_id: genTempId(),
        global_shelf_name: name,
        global_shelf_color_code: normalizeColor(dialogState.colorCode),
        global_shelf_order: shelves.length,
        global_shelf_open_level: dialogState.openLevel,
        global_shelf_description: null,
        _isNew: true,
      };
      setShelves([...shelves, newShelf]);
    } else {
      setShelves(
        shelves.map((s) =>
          s.rl_global_shelf_id === dialogState.shelfId
            ? {
                ...s,
                global_shelf_name: name,
                global_shelf_color_code: normalizeColor(dialogState.colorCode),
                global_shelf_open_level: dialogState.openLevel,
              }
            : s
        )
      );
    }
    setDialogState(null);
  };

  const handleDeleteConfirmed = () => {
    if (!deleteTarget || !shelves) return;
    setShelves(shelves.filter((s) => s.rl_global_shelf_id !== deleteTarget.rl_global_shelf_id));
    setDeleteTarget(null);
  };

  const moveShelf = (index: number, direction: -1 | 1) => {
    if (!shelves) return;
    const target = index + direction;
    if (target < 0 || target >= shelves.length) return;
    const next = [...shelves];
    [next[index], next[target]] = [next[target], next[index]];
    setShelves(next);
  };

  const confirmApplyOpenLevelToBooks = (shelfName: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setPropagateConfirm({ shelfName, resolve });
    });
  };

  const handleSave = async () => {
    if (!shelves || !idToken) return;
    setIsSaving(true);
    try {
      const currentIds = new Set(shelves.map((s) => s.rl_global_shelf_id));
      const deletedIds = Array.from(originalIds).filter((id) => !currentIds.has(id));
      for (const id of deletedIds) {
        await deleteShelf(id, idToken);
      }

      const finalShelves: EditableShelf[] = [];
      for (const shelf of shelves) {
        if (shelf._isNew) {
          const created = await createShelf(
            {
              shelfName: shelf.global_shelf_name,
              colorCode: shelf.global_shelf_color_code,
              openLevel: shelf.global_shelf_open_level,
            },
            idToken
          );
          finalShelves.push({ ...shelf, rl_global_shelf_id: created.rl_global_shelf_id, _isNew: false });
        } else {
          const newOpenLevel = shelf.global_shelf_open_level;
          const openLevelChanged = newOpenLevel !== (originalOpenLevels[shelf.rl_global_shelf_id] ?? 'private');

          await updateShelf(
            {
              shelfId: shelf.rl_global_shelf_id,
              shelfName: shelf.global_shelf_name,
              colorCode: shelf.global_shelf_color_code,
              openLevel: newOpenLevel,
            },
            idToken
          );

          if (openLevelChanged) {
            const applyToBooks = await confirmApplyOpenLevelToBooks(shelf.global_shelf_name);
            if (applyToBooks) {
              await setShelfBooksOpenLevel(shelf.rl_global_shelf_id, newOpenLevel, idToken);
            }
          }
          finalShelves.push(shelf);
        }
      }

      if (finalShelves.length > 0) {
        await reorderShelves(
          finalShelves.map((s) => s.rl_global_shelf_id),
          idToken
        );
      }

      setSnackbar({ open: true, message: '本棚を更新しました', severity: 'success' });
      navigate('/bookshelf');
    } catch (e) {
      setIsSaving(false);
      setSnackbar({ open: true, message: 'エラーが発生しました', severity: 'error' });
    }
  };

  const swatchList = (selected: string, onSelect: (c: string) => void) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, mb: 1 }}>
      {BASIC_COLORS.map((c) => (
        <Box
          key={c}
          onClick={() => onSelect(c)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: `#${c}`,
            cursor: 'pointer',
            border: selected.toUpperCase() === c.toUpperCase() ? '2px solid #000' : '2px solid transparent',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
          }}
        />
      ))}
      <Box
        component="label"
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed rgba(0,0,0,0.35)',
          fontSize: 10,
          color: TOKENS.textSub,
          background: TOKENS.buttonBg,
        }}
      >
        他
        <input
          type="color"
          value={normalizeColor(selected)}
          onChange={(e) => onSelect(e.target.value.replace('#', ''))}
          style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 12 }}>
      <Box sx={{ maxWidth: 640, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text }}>本棚編集</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={openAddDialog}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: 12,
              color: TOKENS.text,
              borderColor: TOKENS.borderSoft,
              '&:hover': { borderColor: TOKENS.accent, background: TOKENS.glassStrong },
            }}
          >
            本棚を追加
          </Button>
        </Box>

        {shelves === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress size={28} sx={{ color: TOKENS.textSub }} />
          </Box>
        ) : shelves.length === 0 ? (
          <Typography sx={{ color: TOKENS.textSub, fontSize: 13, textAlign: 'center', mt: 6 }}>
            本棚の登録がありません
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {shelves.map((shelf, index) => (
              <Box
                key={shelf.rl_global_shelf_id}
                sx={{
                  border: `1px solid ${TOKENS.border}`,
                  background: TOKENS.glass,
                  borderRadius: '14px',
                  p: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Box
                  onClick={() => openEditDialog(shelf)}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: shelf.global_shelf_color_code || '#808080',
                    flexShrink: 0,
                    cursor: 'pointer',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEditDialog(shelf)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        color: TOKENS.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {shelf.global_shelf_name}
                    </Typography>
                    {shelf.global_shelf_open_level === 'public' ? (
                      <PublicIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="公開" />
                    ) : (
                      <LockIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="非公開" />
                    )}
                  </Box>
                  {typeof shelf.book_count === 'number' && (
                    <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>{shelf.book_count} 冊</Typography>
                  )}
                </Box>
                <IconButton size="small" disabled={index === 0} onClick={() => moveShelf(index, -1)}>
                  <ArrowUpwardIcon sx={{ fontSize: 16, color: index === 0 ? TOKENS.borderSoft : TOKENS.textSub }} />
                </IconButton>
                <IconButton size="small" disabled={index === shelves.length - 1} onClick={() => moveShelf(index, 1)}>
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: index === shelves.length - 1 ? TOKENS.borderSoft : TOKENS.textSub }}
                  />
                </IconButton>
                <IconButton size="small" onClick={() => setDeleteTarget(shelf)}>
                  <DeleteOutlineIcon sx={{ fontSize: 18, color: TOKENS.error }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* 下部固定アクションバー */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: TOKENS.glassStrong,
          borderTop: `1px solid ${TOKENS.border}`,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.25, width: '100%', maxWidth: 640 }}>
          <Button
            variant="outlined"
            disabled={isSaving}
            onClick={() => navigate('/bookshelf')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              color: TOKENS.text,
              borderColor: TOKENS.borderSoft,
              px: 3,
              minWidth: 120,
              whiteSpace: 'nowrap',
            }}
          >
            キャンセル
          </Button>
          <Button
            fullWidth
            disabled={isSaving || shelves === null}
            onClick={handleSave}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              color: TOKENS.bg,
              background: TOKENS.text,
              '&:hover': { background: TOKENS.textSub },
              '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
            }}
          >
            {isSaving ? <CircularProgress size={20} sx={{ color: TOKENS.bg }} /> : '本棚編集完了'}
          </Button>
        </Box>
      </Box>

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogState !== null} onClose={() => setDialogState(null)} fullWidth maxWidth="xs">
        <DialogTitle>{dialogState?.mode === 'add' ? '新しい本棚を追加' : '本棚を編集'}</DialogTitle>
        <DialogContent>
          <TextField
            label="名前"
            fullWidth
            variant="outlined"
            value={dialogState?.name ?? ''}
            onChange={(e) => setDialogState((s) => (s ? { ...s, name: e.target.value.slice(0, 20) } : s))}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 20 }}
          />
          {dialogState && swatchList(dialogState.colorCode, (c) => setDialogState((s) => (s ? { ...s, colorCode: c } : s)))}
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <InputLabel>公開設定</InputLabel>
            <Select
              label="公開設定"
              value={dialogState?.openLevel ?? 'private'}
              onChange={(e) => setDialogState((s) => (s ? { ...s, openLevel: e.target.value as string } : s))}
            >
              {Object.entries(OPEN_LEVEL_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogState(null)} sx={{ color: TOKENS.textSub, textTransform: 'none' }}>
            キャンセル
          </Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              color: TOKENS.bg,
              background: TOKENS.text,
              '&:hover': { background: TOKENS.textSub },
            }}
          >
            {dialogState?.mode === 'add' ? '追加' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            本棚を削除すると、この本棚に登録されているすべての本が本棚登録のない状態になります。本棚を削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: TOKENS.textSub, textTransform: 'none' }}>
            キャンセル
          </Button>
          <Button onClick={handleDeleteConfirmed} color="error" sx={{ textTransform: 'none' }}>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 公開設定を本棚内の本にも揃えるかの確認（セーフ方向：いいえを右に通常サイズ、はいを左に小さく） */}
      <Dialog open={propagateConfirm !== null} disableEscapeKeyDown>
        <DialogTitle>公開設定の変更</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            「{propagateConfirm?.shelfName}」に入っている本の公開設定も、本棚と同じ設定に揃えますか？
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            size="small"
            sx={{ fontSize: 12, color: TOKENS.textSub, minWidth: 0, p: 0.5 }}
            onClick={() => {
              propagateConfirm?.resolve(true);
              setPropagateConfirm(null);
            }}
          >
            はい
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              propagateConfirm?.resolve(false);
              setPropagateConfirm(null);
            }}
          >
            いいえ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ShelfManagementPage;
